(() => {
  /* global Swiper */
  const CAROUSEL_NAME = "quick-links-carousel";
  const SLIDE_CLASS = "swiper-slide";
  const DEFAULT_GAP = 8;

  class QuickLinksCarousel extends HTMLElement {
    #swiper = null;
    #initAttempts = 0;
    #initTimer = null;
    #resizeObserver = null;

    connectedCallback() {
      this.#initAttempts = 0;
      this.#init();
    }

    disconnectedCallback() {
      if (this.#initTimer !== null) {
        window.clearTimeout(this.#initTimer);
        this.#initTimer = null;
      }

      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;

      this.#swiper?.destroy(true, true);
      this.#swiper = null;
      this.#initAttempts = 0;
    }

    #init() {
      if (this.#swiper || !this.isConnected) return;
      if (this.dataset.carousel !== "true") return;

      if (typeof Swiper === "undefined") {
        if (this.#initAttempts > 40) return;

        this.#initAttempts += 1;
        this.#initTimer = window.setTimeout(() => {
          this.#initTimer = null;
          this.#init();
        }, 50);
        return;
      }

      const swiperElement = this.querySelector(
        ".collection-header-section__swiper",
      );
      if (!swiperElement) return;

      this.#prepareSlides(swiperElement);

      const slides = swiperElement.querySelectorAll(
        ".swiper-wrapper > .swiper-slide",
      );
      if (slides.length === 0) return;

      const controlsElement = this.querySelector(
        ".collection-header-section__controls",
      );
      const paginationElement = this.querySelector(
        ".collection-header-section__pagination",
      );
      const prevButton = this.querySelector(
        ".collection-header-section__nav-button--prev",
      );
      const nextButton = this.querySelector(
        ".collection-header-section__nav-button--next",
      );
      const showPagination = this.dataset.pagination === "true";
      const showArrows = this.dataset.arrows === "true";
      const slideLabelTemplate = this.dataset.slideLabelTemplate ?? "";

      this.#swiper = new Swiper(swiperElement, {
        slidesPerView: "auto",
        spaceBetween: this.#getGap(swiperElement),
        // Mirrors the `safe center` fallback: centre only while the row fits.
        centerInsufficientSlides: this.dataset.align === "center",
        grabCursor: true,
        watchOverflow: true,
        roundLengths: true,
        resistanceRatio: 0.85,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        pagination:
          showPagination && paginationElement
            ? {
                el: paginationElement,
                clickable: true,
                renderBullet: (index, className) => {
                  const label = slideLabelTemplate.replace(
                    "__SLIDE__",
                    String(index + 1),
                  );

                  return `<button type="button" class="${className}" aria-label="${label.replaceAll('"', "&quot;")}"></button>`;
                },
              }
            : false,
        navigation:
          showArrows && prevButton && nextButton
            ? {
                prevEl: prevButton,
                nextEl: nextButton,
              }
            : false,
        on: {
          lock: () => this.#syncControlsLock(controlsElement, true),
          unlock: () => this.#syncControlsLock(controlsElement, false),
        },
      });

      this.#syncControlsLock(controlsElement, this.#swiper.isLocked);

      // Card widths follow a section setting and a breakpoint, so the track has
      // to be remeasured whenever the section resizes.
      this.#resizeObserver = new ResizeObserver(() => {
        this.#swiper?.update();
        this.#syncControlsLock(controlsElement, this.#swiper?.isLocked);
      });
      this.#resizeObserver.observe(swiperElement);
    }

    #syncControlsLock(controlsElement, isLocked) {
      if (!controlsElement) return;

      controlsElement.classList.toggle(
        "collection-header-section__controls--locked",
        Boolean(isLocked),
      );
    }

    #prepareSlides(swiperElement) {
      const wrapper = swiperElement.querySelector(".swiper-wrapper");
      if (!wrapper) return;

      for (const block of wrapper.querySelectorAll(":scope > .shopify-block")) {
        // A quick link with no label renders nothing; keep its empty wrapper
        // out of the track so it does not leave a phantom gap.
        if (!block.firstElementChild) {
          block.hidden = true;
          continue;
        }

        block.classList.add(SLIDE_CLASS);
      }
    }

    #getGap(swiperElement) {
      const fromData = Number.parseInt(this.dataset.gap ?? "", 10);
      if (!Number.isNaN(fromData)) return fromData;

      const gap = getComputedStyle(swiperElement)
        .getPropertyValue("--quick-links-gap")
        .trim();
      const parsed = Number.parseInt(gap, 10);

      return Number.isNaN(parsed) ? DEFAULT_GAP : parsed;
    }
  }

  if (!customElements.get(CAROUSEL_NAME)) {
    customElements.define(CAROUSEL_NAME, QuickLinksCarousel);
  }
})();
